import React, { useCallback, useEffect, useState } from 'react';
import { Btn, C, Card } from '../../components/UI';
import { platformApi, ActiveSession } from '../../services/api';

const roleColors: Record<string, [string, string]> = {
    Admin: [C.greenBg, C.greenMid],
    PlatformAdmin: ['#EFE9FE', '#6D28D9'],
    Contractor: ['#FEF3C7', '#92400E'],
    Vendor: ['#DBEAFE', '#1D4ED8'],
    Customer: ['#F5F5F5', '#444'],
};

function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins === 1) return '1 min ago';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m ago`;
}

export default function ActiveSessionsPanel() {
    const [sessions, setSessions] = useState<ActiveSession[]>([]);
    const [windowMinutes, setWindowMinutes] = useState(10);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastChecked, setLastChecked] = useState<Date | null>(null);

    const load = useCallback(async (mins: number) => {
        setLoading(true); setError('');
        try {
            setSessions(await platformApi.getActiveSessions(mins));
            setLastChecked(new Date());
        } catch (e: any) {
            setError(e.message || 'Failed to load active sessions.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(windowMinutes); }, [load, windowMinutes]);

    const byRole = sessions.reduce<Record<string, number>>((acc, s) => {
        acc[s.role] = (acc[s.role] || 0) + 1;
        return acc;
    }, {});

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Active Sessions</h1>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: C.textSub }}>
                        Who's currently using ServiceTrek, across every tenant — check this before restarting or deploying the API, since that drops everyone's live connection.
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <label style={{ fontSize: 12, color: C.textSub }}>Active within</label>
                    <select
                        value={windowMinutes}
                        onChange={e => setWindowMinutes(Number(e.target.value))}
                        style={{ padding: '6px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: '#fff' }}
                    >
                        {[5, 10, 15, 30, 60].map(m => <option key={m} value={m}>{m} min</option>)}
                    </select>
                    <Btn variant="outline" onClick={() => load(windowMinutes)} disabled={loading}>{loading ? 'Checking...' : 'Refresh'}</Btn>
                </div>
            </div>

            {error && <div style={{ color: '#D84040', padding: '12px 16px', background: '#FFF0F0', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

            {!loading && !error && (
                <>
                    <Card style={{
                        padding: '18px 22px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16,
                        borderLeft: `4px solid ${sessions.length === 0 ? C.greenMid : '#f59e0b'}`,
                    }}>
                        <div style={{ fontSize: 32, fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums' }}>
                            {sessions.length}
                        </div>
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                                {sessions.length === 0
                                    ? `No one active in the last ${windowMinutes} minutes — safe to deploy.`
                                    : `${sessions.length} ${sessions.length === 1 ? 'person' : 'people'} active in the last ${windowMinutes} minutes`}
                            </div>
                            {sessions.length > 0 && (
                                <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>
                                    {Object.entries(byRole).map(([role, count]) => `${count} ${role}${count !== 1 ? 's' : ''}`).join(' · ')}
                                </div>
                            )}
                            {lastChecked && (
                                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                                    Checked {lastChecked.toLocaleTimeString()} — presence resets on API restart, so this only reflects activity since the last deploy.
                                </div>
                            )}
                        </div>
                    </Card>

                    {sessions.length > 0 && (
                        <Card style={{ padding: 0, overflow: 'hidden' }}>
                            {sessions.map((s, i) => {
                                const [bg, fg] = roleColors[s.role] || ['#F5F5F5', '#444'];
                                return (
                                    <div key={s.userId} style={{
                                        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                                        borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                                    }}>
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                                            background: bg, color: fg, flexShrink: 0, minWidth: 84, textAlign: 'center',
                                        }}>{s.role}</span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{s.name || s.email || 'Unknown'}</div>
                                            <div style={{ fontSize: 12, color: C.textSub }}>
                                                {s.email}{s.tenantName ? ` · ${s.tenantName}` : ''}{!s.tenantName && s.role === 'PlatformAdmin' ? ' · Platform' : ''}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{timeAgo(s.lastSeenAt)}</div>
                                            <div style={{ fontSize: 11, color: C.textMuted }}>{s.ipAddress}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </Card>
                    )}
                </>
            )}
        </>
    );
}
